import { RefObject, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Drawer, DrawerContent } from 'components/ui/drawer';
import { useJoinedPanel } from 'layout/NewDesignLayout/joinedPanels';
import { DETACHED_DRAWER_CLASS } from 'layout/NewDesignLayout/panelBox';
import { cn } from 'utils/cn';
import type { CommentsSession } from './commentsSession';
import { InvoiceCommentsView } from './InvoiceCommentsView';

/**
 * Beside the AI panel's side dock the drawer's end moves in by the dock's width, and it slides out past it. The new
 * design's gap is the drawer's own inset, so it is not added here.
 */
const BESIDE_AI_DOCK =
    'data-[swipe-direction=right]:right-[var(--ai-side-dock-width,0px)] data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+var(--drawer-inset,0px)+var(--ai-side-dock-width,0px)+2px),0,0)]';

type Props = {
    invoiceId: number;
    open: boolean;
    onClose: () => void;
    /** Where the focus goes back to when the drawer closes: the Comments button. */
    returnFocus: RefObject<HTMLElement | null>;
    linkedCommentId: number | null;
    session: CommentsSession;
    /** Docking on: the drawer stands in for the column, and stays out of the new design's one-panel rule. */
    docked: boolean;
    /** Dock, where there is room for the column. */
    onDock?: () => void;
    /** Opened by Undock: the focus starts on Dock, in Undock's place. */
    focusDock: boolean;
};

/**
 * The comments beside the invoice. Non-modal, so the invoice stays usable while it is open, and a click on the
 * invoice leaves it open: it is there to read while working.
 */
export function InvoiceCommentsDrawer({
    invoiceId,
    open,
    onClose,
    returnFocus,
    linkedCommentId,
    session,
    docked,
    onDock,
    focusDock,
}: Props) {
    const intl = useIntl();
    const isNewDesign = useIsNewDesign();
    // In the new design it is one of the side panels of which one is open at a time, while docking is off.
    useJoinedPanel(open && !docked, onClose);
    const popup = useRef<HTMLDivElement | null>(null);
    // Focus that moved on as the drawer closed, to the column that replaced it or another panel, stays there.
    const finalFocus = () => {
        const active = document.activeElement;
        return !active || active === document.body || popup.current?.contains(active) ? returnFocus.current : false;
    };
    return (
        <Drawer
            open={open}
            onOpenChange={(next) => !next && onClose()}
            swipeDirection="right"
            modal={false}
            disablePointerDismissal
        >
            <DrawerContent
                ref={popup}
                aria-label={intl.formatMessage({ id: 'invoice-comments.title' })}
                initialFocus={focusDock ? () => document.querySelector<HTMLElement>('[data-comments-dock]') : undefined}
                finalFocus={finalFocus}
                // Non-modal and closed by nothing outside it, so its tags get the peek card a dialog otherwise opts out of.
                data-peek="on"
                className={
                    isNewDesign
                        ? cn(DETACHED_DRAWER_CLASS, 'w-[440px]', BESIDE_AI_DOCK)
                        : cn(
                              'bg-paper shadow-peek w-[440px] max-w-full data-[swipe-direction=right]:rounded-none data-[swipe-direction=right]:border-0',
                              BESIDE_AI_DOCK
                          )
                }
            >
                <InvoiceCommentsView
                    invoiceId={invoiceId}
                    onClose={onClose}
                    linkedCommentId={linkedCommentId}
                    session={session}
                    host="drawer"
                    dock={onDock ? { docked: false, onToggle: onDock } : undefined}
                />
            </DrawerContent>
        </Drawer>
    );
}
